import React, { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import DocumentTile from "@/components/DocumentTile";
import { toast } from "sonner";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState("");
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.get("/crm/documents"), api.get("/crm/cases")]);
      setDocs(d.data); setCases(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFor) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/crm/documents`, fd, { params: { case_id: uploadingFor }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uppladdat");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const filtered = docs.filter((d) => !q || d.filename.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Dokumentbibliotek</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Dokument
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={uploadingFor}
            onChange={(e) => setUploadingFor(e.target.value)}
            data-testid="upload-case-select"
            className="px-3 py-2.5 border border-zinc-300 rounded-sm text-sm bg-white outline-none focus:border-blue-700"
          >
            <option value="">Välj ärende för uppladdning</option>
            {cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.ref} – {c.title}</option>)}
          </select>
          <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition ${
            uploadingFor ? "bg-blue-700 hover:bg-blue-800 text-white cursor-pointer" : "bg-zinc-200 text-zinc-500 cursor-not-allowed"
          }`}>
            <Upload className="w-4 h-4" /> Ladda upp
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={!uploadingFor}
              onChange={upload}
              data-testid="upload-input"
              className="hidden"
            />
          </label>
        </div>
      </div>

      <div className="mt-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input
          data-testid="search-docs"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Sök dokument..."
          className="w-full pl-10 pr-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white"
        />
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm py-16 text-center text-sm text-zinc-500">
            Inga dokument ännu. Välj ett ärende och ladda upp en fil (PDF, JPG, PNG).
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((d) => <DocumentTile key={d.doc_id} doc={d} />)}
          </div>
        )}
      </div>
    </div>
  );
}
